import React, { FC, useCallback } from 'react';

import classNames from 'classnames';

import { EvSection, EvTitle } from '@evinced-private/ui-common';

import {
	WFA_DASHBOARD_PAGE_TABLE_SECTION_ID,
	WFA_DASHBOARD_PAGE_TABLE_TITLE_ID
} from 'data-automation-ids/WfaDashboardAutomationIds';
import { IWfaSessionRow } from 'src/apps/wfa/services/WfaDashboardServiceTypes';
import { LabelsManagementTableActions } from 'src/common/components/labels-management-table-actions';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { WFA_SESSIONS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import {
	ADD_LABELS_BUTTON_LABEL,
	CHANGE_LABEL_VALUE_BUTTON_LABEL,
	DATA_TYPE,
	REMOVE_LABELS_BUTTON_LABEL,
	SELECT_SESSIONS_PROMPT,
	TABLE_CAPTION,
	TABLE_KEY_FIELD,
	TABLE_TITLE
} from './WfaSessions.constants';
import { IWfaDashboardTableProps } from './WfaSessions.types';
import { getWfaSessionsColumns } from './WfaSessionsTableHelper';

import './WfaSessionsTable.scss';

export const WfaSessionsTable: FC<IWfaDashboardTableProps> = ({
	sessionsQuery,
	unfilteredTotal,
	isLabelsToggleEnabled,
	isNewPlatformEnabled,
	unitIdDisplayName,
	unitIdLabelConfig,
	environmentDisplayName,
	environmentLabelConfig,
	selectedRows,
	tableKey,
	onSelectRow,
	onSelectAll,
	onClearSelection
}) => {
	const total: number = sessionsQuery.data?.total ?? 0;

	const isTableLoading =
		sessionsQuery.isLoading ||
		sessionsQuery.isPreviousData ||
		(sessionsQuery.isStale && sessionsQuery.isRefetching);

	const { tenantConfiguredLabels } = usePlatformConfigStore();

	const handleProcessingComplete = useCallback(async () => {
		await sessionsQuery.refetch();
		onClearSelection();
	}, [sessionsQuery, onClearSelection]);

	return (
		<EvSection
			ariaLabel="Web Flow Analyzer sessions Table"
			skipLinkId="main-section"
			className={classNames('dashboard-section', 'wfa-sessions-table')}
			dataAutomationId={WFA_DASHBOARD_PAGE_TABLE_SECTION_ID}
		>
			<EvTitle
				titleText="Uploaded sessions"
				headingLevel={2}
				className="page-heading"
				dataAutomationId={WFA_DASHBOARD_PAGE_TABLE_TITLE_ID}
			/>

			{isNewPlatformEnabled && (
				<LabelsManagementTableActions<IWfaSessionRow>
					selectedRows={selectedRows}
					handleClearSelection={onClearSelection}
					promptText={SELECT_SESSIONS_PROMPT}
					itemName="session"
					addLabelButtonLabel={ADD_LABELS_BUTTON_LABEL}
					changeLabelValueButtonLabel={CHANGE_LABEL_VALUE_BUTTON_LABEL}
					removeLabelsButtonLabel={REMOVE_LABELS_BUTTON_LABEL}
					onLabelActionSuccess={handleProcessingComplete}
					tenantConfiguredLabels={tenantConfiguredLabels}
					appType="wfa"
				/>
			)}
			{isTableLoading ? (
				<TableSkeleton columns={5} rowCount={7} />
			) : (
				<PlatformTable
					key={tableKey}
					isTableLoading={isTableLoading}
					tableId={WFA_SESSIONS_TABLE_ID}
					tableData={sessionsQuery.data?.rows}
					totalPaginationableResults={total}
					totalCount={unfilteredTotal}
					persistTableState
					columns={getWfaSessionsColumns(
						isLabelsToggleEnabled,
						isNewPlatformEnabled,
						unitIdDisplayName,
						unitIdLabelConfig,
						environmentDisplayName,
						environmentLabelConfig
					)}
					options={{
						keyField: TABLE_KEY_FIELD,
						title: TABLE_TITLE,
						caption: TABLE_CAPTION,
						pagination: true,
						dataType: DATA_TYPE,
						remote: true,
						selectRow: isNewPlatformEnabled
							? {
									mode: 'checkbox',
									onSelect: onSelectRow,
									onSelectAll
								}
							: undefined
					}}
				/>
			)}
		</EvSection>
	);
};
